import type { DiferencaComissao, LancamentoComissao, Meta, Pedido, Representada, SubPedido } from '../db/tipos'
import { arredondar2 } from '../pedidos/precos'
import { STATUS_VENDA } from '../metas/statusVenda'
import { cartoesPorFornecedora, mesDaComissao } from './conferencia'

// ▶ Os numeros da tela Comissoes em painel (spec 2026-10-04). Puro: recebe as
// listas ja lidas. "A receber"/debito/credito continuam em conferencia.ts.

const MESES_CURTOS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']

export function rotuloMesCurto(mes: string): string {
  const [ano, numero] = mes.split('-').map(Number)
  return `${MESES_CURTOS[numero - 1]}/${String(ano).slice(2)}`
}

const vivo = <T extends { apagado_em: string | null }>(x: T) => x.apagado_em === null

// Decisao dela 04/10: o mes FECHA quando o vendedor recebeu a comissao dele.
// Debito/credito que sobra nao segura -- passa adiante com "ref.".
// subsConhecidos: lancamento cujo sub-pedido ainda nao sincronizou nao conta --
// o cartao tambem o pula, e o selo dizia "Em aberto" sem card nenhum embaixo.
// Decisao dela 08/10: o mes corrente (mesHoje, 'AAAA-MM') nunca fecha -- o
// vendedor ainda esta vendendo. Fica "andamento" ate o mes acabar.
export function seloDoMes(
  lancamentos: LancamentoComissao[], mes: string, subsConhecidos: Set<string>, mesHoje: string,
): 'fechado' | 'aberto' | 'sem_comissao' | 'andamento' {
  if (mes >= mesHoje) return 'andamento'
  const vivos = lancamentos.filter((l) => vivo(l) && subsConhecidos.has(l.sub_pedido_id))
  if (!vivos.some((l) => mesDaComissao(l) === mes)) return 'sem_comissao'
  const sobra = vivos.some((l) => {
    const dele = mesDaComissao(l)
    return l.status === 'liberada' && dele !== null && dele <= mes
  })
  return sobra ? 'aberto' : 'fechado'
}

export type VendasDoMes = {
  pedidos: number
  faturamento: number
  porRepresentada: Map<string, { pedidos: number; faturamento: number }>
}

// Mesma regra de venda das metas (metas/metasDados.ts): pedido fechado no mes,
// sub-pedido vivo em STATUS_VENDA. Assim a barra bate com a tela Metas.
export function vendasDoMes(pedidos: Pedido[], subPedidos: SubPedido[], mes: string): VendasDoMes {
  const doMes = new Map(
    pedidos.filter((p) => vivo(p) && p.status === 'fechado' && p.data.slice(0, 7) === mes).map((p) => [p.id, p]),
  )
  const pedidosComVenda = new Set<string>()
  const porRepresentada = new Map<string, { pedidos: number; faturamento: number; ids: Set<string> }>()
  let faturamento = 0
  for (const s of subPedidos) {
    if (!vivo(s) || !STATUS_VENDA.includes(s.status) || !doMes.has(s.pedido_id)) continue
    pedidosComVenda.add(s.pedido_id)
    faturamento += s.valor_total
    const grupo = porRepresentada.get(s.representada_id) ?? { pedidos: 0, faturamento: 0, ids: new Set<string>() }
    grupo.ids.add(s.pedido_id)
    grupo.faturamento += s.valor_total
    porRepresentada.set(s.representada_id, grupo)
  }
  return {
    pedidos: pedidosComVenda.size,
    faturamento: arredondar2(faturamento),
    porRepresentada: new Map([...porRepresentada].map(([id, g]) => [id, { pedidos: g.ids.size, faturamento: arredondar2(g.faturamento) }])),
  }
}

export function recebidaNoMes(
  lancamentos: LancamentoComissao[], subPedidos: SubPedido[], mes: string,
): { total: number; porRepresentada: Map<string, number> } {
  const repDoSub = new Map(subPedidos.map((s) => [s.id, s.representada_id]))
  const porRepresentada = new Map<string, number>()
  let total = 0
  for (const l of lancamentos) {
    if (!vivo(l) || l.status !== 'recebida' || mesDaComissao(l) !== mes) continue
    const valor = l.valor_recebido ?? 0
    total += valor
    const rep = repDoSub.get(l.sub_pedido_id)
    if (rep !== undefined) porRepresentada.set(rep, arredondar2((porRepresentada.get(rep) ?? 0) + valor))
  }
  return { total: arredondar2(total), porRepresentada }
}

export function variacao(atual: number, anterior: number): { texto: string; direcao: 'alta' | 'queda' | 'igual' | 'sem_base' } {
  if (anterior === 0) return { texto: '—', direcao: 'sem_base' }
  const pct = Math.round(((atual - anterior) / anterior) * 1000) / 10
  if (pct === 0) return { texto: '=', direcao: 'igual' }
  const numero = Math.abs(pct).toFixed(1).replace('.', ',')
  return pct > 0 ? { texto: `+${numero}%`, direcao: 'alta' } : { texto: `-${numero}%`, direcao: 'queda' }
}

export function metasDoMes(metas: Meta[], mes: string): Meta[] {
  const inicio = `${mes}-01`
  const fim = `${mes}-31`
  // Decisao dela 04/10: so meta que comeca E termina dentro do mes. A trimestral
  // mostraria o andamento do trimestre ao lado do faturamento do mes; ela fica
  // so na tela Metas.
  return metas.filter((m) => vivo(m) && m.periodo_inicio >= inicio && m.periodo_fim <= fim)
}

export function progressoSomado(metas: Meta[], realizados: Map<string, number>): { feito: number; alvo: number; pct: number } | null {
  if (metas.length === 0) return null
  const feito = arredondar2(metas.reduce((s, m) => s + (realizados.get(m.id) ?? 0), 0))
  const alvo = arredondar2(metas.reduce((s, m) => s + m.valor_alvo, 0))
  return { feito, alvo, pct: alvo > 0 ? Math.round((feito / alvo) * 100) : 0 }
}

// Meta com alvo 0 fica fora do total: nunca seria contada como atingida.
export function contarAtingidas(metas: Meta[], realizados: Map<string, number>): { atingidas: number; total: number } {
  const comAlvo = metas.filter((m) => m.valor_alvo > 0)
  return {
    atingidas: comAlvo.filter((m) => (realizados.get(m.id) ?? 0) >= m.valor_alvo).length,
    total: comAlvo.length,
  }
}

export function corDaBarra(pct: number): 'verde' | 'verde_claro' | 'laranja' {
  if (pct >= 100) return 'verde'
  if (pct < 80) return 'laranja'
  return 'verde_claro'
}

// Decisao dela 04/10: "valor vendido" e "faturamento total" medem a mesma venda;
// havendo as duas no mesmo recorte, vale a de MAIOR alvo (somar dobrava a meta).
export function progressoDaMaior(metas: Meta[], realizados: Map<string, number>): { feito: number; alvo: number; pct: number } | null {
  if (metas.length === 0) return null
  const maior = metas.reduce((a, b) => (b.valor_alvo > a.valor_alvo ? b : a))
  return progressoSomado([maior], realizados)
}

export type NumerosDoResumo = {
  pedidos: number; faturamento: number; aReceber: number; recebida: number
  pedidosAntes: number; faturamentoAntes: number; recebidaAntes: number
}

// Decisao dela 04/10: o Resumo do mes tem filtro -- 'geral' ou o id de uma
// fornecedora. So o Resumo muda; a lista de baixo continua com todas.
export function numerosDoResumo(
  filtro: string,
  dados: {
    vendas: VendasDoMes; vendasAntes: VendasDoMes
    recebida: { total: number; porRepresentada: Map<string, number> }
    recebidaAntes: { total: number; porRepresentada: Map<string, number> }
    cartoes: Array<{ representadaId: string; aReceber: number }>
  },
): NumerosDoResumo {
  const { vendas, vendasAntes, recebida, recebidaAntes, cartoes } = dados
  if (filtro === 'geral') {
    return {
      pedidos: vendas.pedidos, faturamento: vendas.faturamento,
      aReceber: arredondar2(cartoes.reduce((s, c) => s + c.aReceber, 0)), recebida: recebida.total,
      pedidosAntes: vendasAntes.pedidos, faturamentoAntes: vendasAntes.faturamento, recebidaAntes: recebidaAntes.total,
    }
  }
  const agora = vendas.porRepresentada.get(filtro) ?? { pedidos: 0, faturamento: 0 }
  const antes = vendasAntes.porRepresentada.get(filtro) ?? { pedidos: 0, faturamento: 0 }
  return {
    pedidos: agora.pedidos, faturamento: agora.faturamento,
    aReceber: cartoes.find((c) => c.representadaId === filtro)?.aReceber ?? 0,
    recebida: recebida.porRepresentada.get(filtro) ?? 0,
    pedidosAntes: antes.pedidos, faturamentoAntes: antes.faturamento,
    recebidaAntes: recebidaAntes.porRepresentada.get(filtro) ?? 0,
  }
}

// Teste dela 04/10, item 1: quando o vendedor ja recebeu a comissao (nada a
// receber e algo recebido), o card diz "Recebido R$ X" em vez de "A receber R$ 0,00".
export function destaqueDoCartao(aReceber: number, recebida: number): { rotulo: 'A receber' | 'Recebido'; valor: number; recebidaEmbaixo: boolean } {
  if (aReceber === 0 && recebida > 0) return { rotulo: 'Recebido', valor: recebida, recebidaEmbaixo: false }
  return { rotulo: 'A receber', valor: aReceber, recebidaEmbaixo: true }
}

// A lista de troca de fornecedora da Baixa e a MESMA da tela Comissoes: quem
// tem cartao no mes (inclusive so por diferenca em aberto -- minor 04/10, que a
// lista ignorava) + quem so vendeu. A atual entra sempre.
export function idsParaTroca(d: {
  atual: string; mes: string; representadas: Representada[]
  lancamentos: LancamentoComissao[]; subPedidos: SubPedido[]; pedidos: Pedido[]
  diferencas: DiferencaComissao[]
}): Set<string> {
  const cartoes = cartoesPorFornecedora(d.lancamentos.filter(vivo), d.subPedidos, d.representadas, d.mes, d.diferencas)
  return new Set([
    d.atual, ...cartoes.map((c) => c.representadaId),
    ...vendasDoMes(d.pedidos, d.subPedidos, d.mes).porRepresentada.keys(),
  ])
}
